// CareLogix™ ORACLE — ORACLE AGENT SWARM™ Gen-3 Console (Phase 32A)
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PALETTE, scoreColor } from "@/components/animation/MotionShared";
import { swarmApi } from "@/api/swarmClient";

function AgentBubble({ a }) {
  return (
    <div style={S.agentBubble} data-testid={`p32-swarm-agent-${a.id}`}>
      <span style={{ ...S.agentIcon, color: a.color }}>{a.icon}</span>
      <div style={S.agentMeta}>
        <span style={{ ...S.agentLabel, color: a.color }}>{a.label}</span>
        <span style={S.agentRole}>{a.role}</span>
        <span style={S.agentModel}>{a.model}</span>
      </div>
    </div>
  );
}

function VerdictRing({ verdict, score, agreement }) {
  const color = verdict === "watch_strong" ? PALETTE.greenAccent
    : verdict === "watch"   ? PALETTE.emerald
    : verdict === "consider"? PALETTE.amber
    : verdict === "hold"    ? PALETTE.slate4
    : PALETTE.red;
  return (
    <div style={S.verdictRing} data-testid="p32-swarm-verdict-ring">
      <span style={{ ...S.verdictLabel, color }} data-testid="p32-swarm-verdict-label">{(verdict || "—").replace("_", " ").toUpperCase()}</span>
      <span style={{ ...S.verdictScore, color }} data-testid="p32-swarm-verdict-score">{Math.round(score || 0)}</span>
      <span style={S.verdictSub}>Agreement: {Math.round(agreement || 0)}%</span>
    </div>
  );
}

function NisiBar({ label, value, color }) {
  const c = color || scoreColor(value);
  const w = Math.max(2, Math.min(100, Number(value) || 0));
  return (
    <div style={S.nisiRow}>
      <div style={S.nisiHead}>
        <span style={S.nisiLbl}>{label}</span>
        <span style={{ ...S.nisiVal, color: c }}>{Math.round(Number(value) || 0)}</span>
      </div>
      <div style={S.nisiBar}>
        <motion.div
          style={{ ...S.nisiBarFill, background: c }}
          initial={{ width: "0%" }}
          animate={{ width: `${w}%` }}
          transition={{ duration: 0.9, ease: "easeOut" }}
        />
      </div>
    </div>
  );
}

function AgentTurnCard({ turn, kind }) {
  return (
    <motion.li
      style={{ ...S.turn, borderColor: turn.color }}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
      data-testid={`p32-swarm-turn-${turn.agent}-${kind}`}
    >
      <header style={S.turnHead}>
        <span style={{ ...S.turnIcon, color: turn.color }}>{turn.icon}</span>
        <span style={{ ...S.turnLabel, color: turn.color }}>{turn.label}</span>
        <span style={S.turnKind}>{kind === "draft" ? "Draft" : kind === "debate" ? "Debate" : "Audit"}</span>
        {turn.offline && <span style={S.turnOffline}>OFFLINE-SAFE</span>}
        <span style={S.turnModel}>{turn.model}</span>
      </header>
      <pre style={S.turnText}>{turn.text}</pre>
    </motion.li>
  );
}

export default function AgentSwarmConsole({ testid = "p32-swarm-console" }) {
  const [agents, setAgents] = useState([]);
  const [llmAvailable, setLlmAvailable] = useState(true);
  const [topic, setTopic] = useState("NVDA outlook next two weeks — setup, risk, catalysts");
  const [maxSpecialists, setMaxSpecialists] = useState(3);
  const [mode, setMode] = useState("balanced");
  const [riskThreshold, setRiskThreshold] = useState(70);
  const [trace, setTrace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAgentRegistry, setShowAgentRegistry] = useState(false);

  useEffect(() => {
    swarmApi.agents().then((r) => {
      setAgents(r.data?.agents || []);
      setLlmAvailable(Boolean(r.data?.llm_available));
    }).catch(() => { /* ignore */ });
  }, []);

  const submit = async () => {
    if (!topic || topic.trim().length < 4) {
      setError("Please provide a topic of at least 4 characters.");
      return;
    }
    setLoading(true);
    setError(null);
    setTrace(null);
    try {
      const r = await swarmApi.query({
        topic: topic.trim(),
        mode,
        max_specialists: Number(maxSpecialists) || 3,
        risk_threshold:  Number(riskThreshold) || 70,
      });
      setTrace(r.data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        setError("Sign in as a member to run the Agent Swarm.");
      } else if (status === 403) {
        setError("Guests cannot run the Agent Swarm. Please sign in.");
      } else {
        setError("Agent Swarm temporarily unavailable. Please retry.");
      }
    } finally {
      setLoading(false);
    }
  };

  const drafts = useMemo(() => trace?.drafts || [], [trace]);
  const debate = useMemo(() => trace?.debate || [], [trace]);
  const compliance = trace?.compliance || null;
  const consensus  = trace?.consensus  || {};
  const nisi       = trace?.nisi       || {};
  const rec        = trace?.recommendation || {};

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.header}>
        <div>
          <h2 style={S.title} data-testid={`${testid}-title`}>ORACLE AGENT SWARM™ GEN-3</h2>
          <p style={S.subtitle} data-testid={`${testid}-subtitle`}>
            12 CareLogix-native agents debate, dissent, and converge — with Compliance Lock™ and HITL approval gate. {!llmAvailable && " (offline-safe mode)"}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAgentRegistry((v) => !v)}
          style={S.regBtn}
          data-testid={`${testid}-toggle-registry`}
        >
          {showAgentRegistry ? "Hide" : "View"} agent registry ({agents.length})
        </button>
      </header>

      <AnimatePresence>
        {showAgentRegistry && (
          <motion.div
            key="reg"
            style={S.registry}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            data-testid={`${testid}-registry`}
          >
            {agents.map((a) => <AgentBubble a={a} key={a.id} />)}
          </motion.div>
        )}
      </AnimatePresence>

      <div style={S.queryRow} data-testid={`${testid}-query-row`}>
        <textarea
          value={topic}
          onChange={(ev) => setTopic(ev.target.value)}
          rows={2}
          maxLength={600}
          placeholder="Ask the swarm anything (e.g., 'Bitcoin breakout outlook next two weeks')"
          style={S.textarea}
          data-testid={`${testid}-topic-input`}
        />
        <div style={S.controls}>
          <label style={S.ctrlLabel}>
            <span style={S.ctrlLbl}>Mode</span>
            <select value={mode} onChange={(e) => setMode(e.target.value)} style={S.select} data-testid={`${testid}-mode`}>
              <option value="balanced">balanced</option>
              <option value="aggressive">aggressive</option>
              <option value="conservative">conservative</option>
            </select>
          </label>
          <label style={S.ctrlLabel}>
            <span style={S.ctrlLbl}>Max specialists</span>
            <select value={maxSpecialists} onChange={(e) => setMaxSpecialists(Number(e.target.value))} style={S.select} data-testid={`${testid}-max-specialists`}>
              {[2,3,4,5,6].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label style={S.ctrlLabel}>
            <span style={S.ctrlLbl}>Risk threshold</span>
            <input
              type="number" min={30} max={95} value={riskThreshold}
              onChange={(e) => setRiskThreshold(Number(e.target.value))}
              style={S.input} data-testid={`${testid}-risk-threshold`}
            />
          </label>
          <button type="button" onClick={submit} style={S.submitBtn} disabled={loading} data-testid={`${testid}-submit`}>
            {loading ? "Running Swarm…" : "Run Swarm"}
          </button>
        </div>
      </div>

      {error && (
        <div style={S.err} data-testid={`${testid}-error`}>{error}</div>
      )}

      {loading && (
        <div style={S.loading} data-testid={`${testid}-loading`}>
          Agents drafting opinions in parallel… debate and consensus follow. This can take 20–60 seconds.
        </div>
      )}

      {trace && (
        <div style={S.trace} data-testid={`${testid}-trace`}>
          <header style={S.traceHead}>
            <span style={S.traceTopic} data-testid={`${testid}-trace-topic`}>{trace.topic}</span>
            <span style={S.traceMeta}>
              {trace.specialists?.length || 0} specialists · status: <strong style={{ color: trace.status === "approved" ? PALETTE.greenAccent : PALETTE.amber }}>{trace.status}</strong>
            </span>
          </header>

          <div style={S.consensusGrid} data-testid={`${testid}-consensus`}>
            <VerdictRing verdict={consensus.verdict} score={consensus.score} agreement={consensus.agreement} />
            <div style={S.nisiGrid}>
              <NisiBar label="Opportunity" value={nisi.opportunity_score} />
              <NisiBar label="Risk" value={nisi.risk_score} color={PALETTE.red} />
              <NisiBar label="Confidence" value={nisi.confidence_score} />
              <NisiBar label="Volatility" value={nisi.volatility_score} color={PALETTE.amber} />
            </div>
          </div>

          <div style={S.execRec} data-testid={`${testid}-exec-recommendation`}>
            <h3 style={S.execTitle}>Executive Recommendation™ (HITL gated)</h3>
            <p style={S.execSummary}>{rec.summary || "—"}</p>
            {rec.requires_admin_approval && (
              <span style={S.adminBadge} data-testid={`${testid}-admin-approval-required`}>Compliance approval required</span>
            )}
          </div>

          <h3 style={S.sectionTitle}>Draft round</h3>
          <ul style={S.turns} data-testid={`${testid}-drafts`}>
            {drafts.map((t, i) => <AgentTurnCard turn={t} kind="draft" key={`d-${i}`} />)}
          </ul>

          <h3 style={S.sectionTitle}>Debate round</h3>
          <ul style={S.turns} data-testid={`${testid}-debate`}>
            {debate.map((t, i) => <AgentTurnCard turn={t} kind="debate" key={`db-${i}`} />)}
          </ul>

          {compliance && (
            <>
              <h3 style={S.sectionTitle}>Compliance audit</h3>
              <ul style={S.turns} data-testid={`${testid}-compliance`}>
                <AgentTurnCard turn={compliance} kind="audit" />
              </ul>
            </>
          )}

          <footer style={S.complianceFoot} data-testid={`${testid}-compliance-foot`}>
            {trace.compliance_lock || "Research only · Not financial advice · No guarantees · Human review required."}
          </footer>
        </div>
      )}
    </section>
  );
}

const S = {
  shell:        { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 18, display: "flex", flexDirection: "column", gap: 14, color: PALETTE.slate3 },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" },
  title:        { margin: 0, color: PALETTE.goldBright, fontSize: 18, letterSpacing: "0.07em" },
  subtitle:     { margin: "4px 0 0 0", color: PALETTE.slate4, fontSize: 12.5, lineHeight: 1.5 },
  regBtn:       { appearance: "none", background: "transparent", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.gold, padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.06em" },
  registry:     { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8, overflow: "hidden" },
  agentBubble:  { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 8, display: "flex", gap: 10, alignItems: "flex-start" },
  agentIcon:    { fontSize: 22, lineHeight: 1, width: 22, textAlign: "center", fontFamily: "'Space Mono', monospace" },
  agentMeta:    { display: "flex", flexDirection: "column", gap: 2 },
  agentLabel:   { fontSize: 12.5, fontWeight: 700, letterSpacing: "0.03em" },
  agentRole:    { color: PALETTE.slate4, fontSize: 10.5, lineHeight: 1.35 },
  agentModel:   { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.06em" },
  queryRow:     { display: "flex", flexDirection: "column", gap: 8 },
  textarea:     { width: "100%", boxSizing: "border-box", background: "#040608", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.slate3, borderRadius: 10, padding: 10, fontSize: 13, fontFamily: "inherit", resize: "vertical" },
  controls:     { display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" },
  ctrlLabel:    { display: "flex", flexDirection: "column", gap: 4 },
  ctrlLbl:      { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.10em" },
  select:       { background: "#040608", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.slate3, padding: "6px 8px", borderRadius: 8, fontSize: 12.5, fontFamily: "inherit" },
  input:        { background: "#040608", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.slate3, padding: "6px 8px", borderRadius: 8, width: 70, fontSize: 12.5, fontFamily: "inherit" },
  submitBtn:    { appearance: "none", background: PALETTE.gold, color: PALETTE.bgDeep, border: "none", padding: "8px 16px", borderRadius: 999, fontWeight: 800, letterSpacing: "0.08em", fontFamily: "'Space Mono', monospace", cursor: "pointer" },
  err:          { color: PALETTE.red, fontSize: 12.5, background: "rgba(239,68,68,0.06)", border: `1px solid ${PALETTE.red}`, padding: "6px 10px", borderRadius: 8 },
  loading:      { color: PALETTE.amber, fontSize: 12.5, background: "rgba(252,211,77,0.06)", border: `1px solid ${PALETTE.amber}`, padding: "6px 10px", borderRadius: 8 },
  trace:        { display: "flex", flexDirection: "column", gap: 12 },
  traceHead:    { display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" },
  traceTopic:   { color: PALETTE.goldBright, fontSize: 14, fontWeight: 700, letterSpacing: "0.03em" },
  traceMeta:    { color: PALETTE.slate4, fontSize: 11.5 },
  consensusGrid:{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 12, alignItems: "center" },
  verdictRing:  { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, background: "#040608", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 12 },
  verdictLabel: { fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.12em" },
  verdictScore: { fontFamily: "'Space Mono', monospace", fontSize: 30, fontWeight: 800 },
  verdictSub:   { color: PALETTE.slate4, fontFamily: "'Space Mono', monospace", fontSize: 10 },
  nisiGrid:     { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 6 },
  nisiRow:      { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, padding: 6 },
  nisiHead:     { display: "flex", justifyContent: "space-between", alignItems: "center" },
  nisiLbl:      { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.10em" },
  nisiVal:      { fontFamily: "'Space Mono', monospace", fontSize: 12, fontWeight: 800 },
  nisiBar:      { marginTop: 4, height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" },
  nisiBarFill:  { height: "100%", width: 0 },
  execRec:      { background: "rgba(197,160,89,0.05)", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 6 },
  execTitle:    { margin: 0, color: PALETTE.goldBright, fontSize: 13, letterSpacing: "0.05em" },
  execSummary:  { margin: 0, color: PALETTE.slate3, fontSize: 12.5, lineHeight: 1.5 },
  adminBadge:   { display: "inline-block", color: PALETTE.amber, border: `1px solid ${PALETTE.amber}`, padding: "3px 8px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em", alignSelf: "flex-start", background: "rgba(252,211,77,0.06)" },
  sectionTitle: { margin: "4px 0 4px 0", color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.12em" },
  turns:        { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 },
  turn:         { background: "#040608", border: "1px solid", borderRadius: 10, padding: 8, display: "flex", flexDirection: "column", gap: 4 },
  turnHead:     { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  turnIcon:     { fontFamily: "'Space Mono', monospace", fontSize: 14, width: 18, textAlign: "center" },
  turnLabel:    { fontSize: 12.5, fontWeight: 700, letterSpacing: "0.03em" },
  turnKind:     { color: PALETTE.slate4, fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.10em" },
  turnOffline:  { color: PALETTE.amber, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.10em" },
  turnModel:    { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.06em", marginLeft: "auto" },
  turnText:     { margin: 0, color: PALETTE.slate3, fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap", fontFamily: "inherit" },
  complianceFoot: { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5, textAlign: "right" },
};
